import React, { useEffect, useState, useRef, createElement } from 'react';
import { PlayIcon, PauseIcon, VolumeIcon, Volume2Icon, VolumeXIcon, MaximizeIcon, MinimizeIcon, RotateCcwIcon, DownloadIcon } from 'lucide-react';
interface VideoMetadata {
  width?: number;
  height?: number;
  isVertical?: boolean;
  duration?: number;
}
interface ResponsiveVideoPlayerProps {
  videoUrl: string;
  title?: string;
  autoPlay?: boolean;
  loop?: boolean;
  showControls?: boolean;
  allowDownload?: boolean;
  className?: string;
  posterImage?: string;
  videoMetadata?: VideoMetadata;
}
export const ResponsiveVideoPlayer: React.FC<ResponsiveVideoPlayerProps> = ({
  videoUrl,
  title = '',
  autoPlay = false,
  loop = true,
  showControls = true,
  allowDownload = false,
  className = '',
  posterImage,
  videoMetadata
}) => {
  // Player state
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [areControlsVisible, setAreControlsVisible] = useState(true);
  const [isVertical, setIsVertical] = useState(videoMetadata?.isVertical || false);
  // References
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Effect to handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  // Effect to hide controls after inactivity
  useEffect(() => {
    if (!showControls) return;
    const handleMouseMove = () => {
      setAreControlsVisible(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setAreControlsVisible(false);
        }
      }, 3000);
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', () => {
        if (isPlaying) {
          setAreControlsVisible(false);
        }
      });
      container.addEventListener('mouseenter', () => {
        setAreControlsVisible(true);
      });
    }
    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showControls, isPlaying]);
  // Handlers
  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
    }
    setIsPlaying(!isPlaying);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };
  const handleMuteToggle = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      // Store the previous volume level before muting
      if (newMutedState) {
        videoRef.current.volume = 0;
      } else {
        videoRef.current.volume = volume || 0.5;
      }
    }
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setDuration(video.duration);
      setIsLoaded(true);
      // Detect orientation if not provided in metadata
      if (!videoMetadata?.isVertical) {
        const detected = video.videoHeight > video.videoWidth;
        setIsVertical(detected);
      }
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTime = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };
  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (loop && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(error => {
        console.error('Error playing video:', error);
      });
      setIsPlaying(true);
    }
  };
  const handleFullscreenToggle = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };
  const handleRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      if (!isPlaying) {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
        setIsPlaying(true);
      }
    }
  };
  const handleDownload = async () => {
    if (videoUrl) {
      try {
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = title || 'video-download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };
  const handleWaiting = () => {
    setIsBuffering(true);
  };
  const handlePlaying = () => {
    setIsBuffering(false);
  };
  // Format time (seconds -> MM:SS)
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  // Determine video container class based on orientation
  const videoContainerClass = isVertical ? 'vertical-video-container' : 'horizontal-video-container';
  return <div ref={containerRef} className={`relative overflow-hidden rounded-lg bg-black ${videoContainerClass} ${className}`} onDoubleClick={handleFullscreenToggle} data-id="element-2935">
      {/* Video Element */}
      <video ref={videoRef} src={videoUrl} poster={posterImage} className={`${isVertical ? 'vertical-video' : 'horizontal-video'} object-contain`} autoPlay={autoPlay} loop={loop} playsInline muted={isMuted} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleVideoEnded} onWaiting={handleWaiting} onPlaying={handlePlaying} onClick={handlePlayPause} data-id="element-2936" />
      {/* Loading overlay */}
      {!isLoaded && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50" data-id="element-2937">
          <div className="w-12 h-12 border-4 border-t-[#2DB489] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" data-id="element-2938"></div>
        </div>}
      {/* Buffering indicator */}
      {isBuffering && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30" data-id="element-2939">
          <div className="w-12 h-12 border-4 border-t-[#2DB489] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" data-id="element-2940"></div>
        </div>}
      {/* Play/Pause overlay button (shows when paused) */}
      {!isPlaying && <button className="absolute inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30 z-10" onClick={handlePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-2941">
          <div className="w-16 h-16 rounded-full bg-[#2DB489] dark:bg-[#4ED1A0] flex items-center justify-center" data-id="element-2942">
            <PlayIcon className="h-8 w-8 text-white" data-id="element-2943" />
          </div>
        </button>}
      {/* Controls */}
      {showControls && <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 transition-opacity duration-300 ${areControlsVisible || !isPlaying ? 'opacity-100' : 'opacity-0'}`} data-id="element-2944">
          {/* Progress bar */}
          <div className="flex items-center mb-2" data-id="element-2945">
            <input type="range" min={0} max={duration || 100} value={currentTime} onChange={handleSeek} className="w-full h-1 bg-gray-500 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#2DB489] dark:[&::-webkit-slider-thumb]:bg-[#4ED1A0]" data-id="element-2946" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2947">
            <div className="flex items-center space-x-3" data-id="element-2948">
              {/* Play/Pause button */}
              <button onClick={handlePlayPause} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-2949">
                {isPlaying ? <PauseIcon className="h-5 w-5" data-id="element-2950" /> : <PlayIcon className="h-5 w-5" data-id="element-2951" />}
              </button>
              {/* Restart button */}
              <button onClick={handleRestart} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label="Restart" data-id="element-2952">
                <RotateCcwIcon className="h-4 w-4" data-id="element-2953" />
              </button>
              {/* Volume control */}
              <div className="relative flex items-center" onMouseEnter={() => setShowVolumeSlider(true)} onMouseLeave={() => setShowVolumeSlider(false)} data-id="element-2954">
                <button onClick={handleMuteToggle} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-2955">
                  {isMuted ? <VolumeXIcon className="h-5 w-5" data-id="element-2956" /> : volume > 0.5 ? <Volume2Icon className="h-5 w-5" data-id="element-2957" /> : <VolumeIcon className="h-5 w-5" data-id="element-2958" />}
                </button>
                {showVolumeSlider && <div className="absolute left-6 bottom-0 w-24 h-8 bg-black bg-opacity-70 rounded-md flex items-center px-2" data-id="element-2959">
                    <input type="range" min={0} max={1} step={0.1} value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="w-full h-1 bg-gray-500 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#2DB489] dark:[&::-webkit-slider-thumb]:bg-[#4ED1A0]" data-id="element-2960" />
                  </div>}
              </div>
              {/* Time display */}
              <div className="text-white text-xs" data-id="element-2961">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-2962">
              {/* Download button */}
              {allowDownload && <button onClick={handleDownload} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label="Download" data-id="element-2963">
                  <DownloadIcon className="h-5 w-5" data-id="element-2964" />
                </button>}
              {/* Fullscreen button */}
              <button onClick={handleFullscreenToggle} className="text-white hover:text-[#2DB489] dark:hover:text-[#4ED1A0] focus:outline-none" aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'} data-id="element-2965">
                {isFullscreen ? <MinimizeIcon className="h-5 w-5" data-id="element-2966" /> : <MaximizeIcon className="h-5 w-5" data-id="element-2967" />}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};